import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLang } from "@/App";
import { copy, CONTACT } from "@/i18n";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full rounded-xl bg-white/[0.04] border border-white/15 focus:border-crimson focus:outline-none transition-colors px-4 py-3 text-sm text-white placeholder:text-neutral-500";

const Reservation = () => {
    const { lang } = useLang();
    const c = copy[lang].reserve;
    const [form, setForm] = useState({ name: "", email: "", phone: "", event_date: "", guests: "", message: "" });
    const [sending, setSending] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const res = await axios.post(`${API}/quote-request`, form);
            if (res.data.status === "success") {
                toast.success(c.success);
                setForm({ name: "", email: "", phone: "", event_date: "", guests: "", message: "" });
            } else {
                toast.error(c.error);
            }
        } catch {
            toast.error(c.error);
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" data-testid="reservation-section" className="relative py-20 lg:py-32 bg-coal/40 border-t border-white/5 overflow-hidden">
            <img
                src="/media/entrata.png"
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover opacity-[0.13] pointer-events-none"
                loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-noir via-noir/60 to-noir pointer-events-none" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_80%,rgba(230,0,35,0.12),transparent_55%)] pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="text-center"
                >
                    <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-crimson">
                        {c.label}
                    </span>
                    <h2 className="mt-4 font-display font-extrabold uppercase tracking-tight text-2xl sm:text-4xl lg:text-5xl">
                        {c.title}
                    </h2>
                </motion.div>

                <div className="mt-14 grid lg:grid-cols-2 gap-8">
                    <motion.div
                        initial={{ opacity: 0, y: 28 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.7 }}
                        data-testid="call-booking-card"
                        className="rounded-2xl border border-white/10 bg-card p-8 sm:p-10 flex flex-col justify-between"
                    >
                        <div>
                            <h3 className="font-display font-bold uppercase tracking-tight text-xl sm:text-2xl">
                                {c.callTitle}
                            </h3>
                            <p className="mt-3 text-neutral-400 text-sm sm:text-base leading-relaxed">{c.callText}</p>
                            <a
                                data-testid="booking-phone-display"
                                href={CONTACT.phoneHref}
                                className="mt-8 block font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-white hover:text-crimson-bright transition-colors"
                            >
                                {CONTACT.phoneDisplay}
                            </a>
                        </div>
                        <div className="mt-10 grid sm:grid-cols-2 gap-3">
                            <a
                                data-testid="call-now-button"
                                href={CONTACT.phoneHref}
                                className="inline-flex items-center justify-center gap-3 rounded-full bg-crimson hover:bg-crimson-bright transition-colors text-white font-bold uppercase tracking-widest text-sm px-6 py-4 glow-red"
                            >
                                <Phone size={16} />
                                {c.callCta}
                            </a>
                            <a
                                data-testid="whatsapp-button"
                                href={CONTACT.whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-3 rounded-full border border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366] hover:text-noir transition-colors font-bold uppercase tracking-widest text-sm px-6 py-4"
                            >
                                <WhatsAppIcon size={17} />
                                {c.whatsappCta}
                            </a>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 28 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        data-testid="quote-form-card"
                        className="rounded-2xl border border-crimson/30 bg-card p-8 sm:p-10"
                    >
                        <h3 className="font-display font-bold uppercase tracking-tight text-xl sm:text-2xl">
                            {c.formTitle}
                        </h3>
                        <p className="mt-3 text-neutral-400 text-sm sm:text-base">{c.formText}</p>

                        <form data-testid="quote-form" onSubmit={submit} className="mt-6 grid gap-4">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <input
                                    data-testid="quote-name-input"
                                    required
                                    value={form.name}
                                    onChange={set("name")}
                                    placeholder={c.name}
                                    className={inputCls}
                                />
                                <input
                                    data-testid="quote-email-input"
                                    required
                                    type="email"
                                    value={form.email}
                                    onChange={set("email")}
                                    placeholder={c.email}
                                    className={inputCls}
                                />
                            </div>
                            <div className="grid sm:grid-cols-3 gap-4">
                                <input
                                    data-testid="quote-phone-input"
                                    value={form.phone}
                                    onChange={set("phone")}
                                    placeholder={c.phone}
                                    className={inputCls}
                                />
                                <input
                                    data-testid="quote-date-input"
                                    type="date"
                                    value={form.event_date}
                                    onChange={set("event_date")}
                                    aria-label={c.date}
                                    className={`${inputCls} [color-scheme:dark]`}
                                />
                                <input
                                    data-testid="quote-guests-input"
                                    type="number"
                                    min="1"
                                    value={form.guests}
                                    onChange={set("guests")}
                                    placeholder={c.guests}
                                    className={inputCls}
                                />
                            </div>
                            <textarea
                                data-testid="quote-message-input"
                                rows={4}
                                value={form.message}
                                onChange={set("message")}
                                placeholder={c.message}
                                className={`${inputCls} resize-none`}
                            />
                            <button
                                data-testid="quote-submit-button"
                                type="submit"
                                disabled={sending}
                                className="inline-flex items-center justify-center gap-3 rounded-full bg-white text-noir hover:bg-crimson hover:text-white transition-colors font-bold uppercase tracking-widest text-sm px-7 py-4 disabled:opacity-60"
                            >
                                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                                {sending ? c.sending : c.submit}
                            </button>
                        </form>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Reservation;
